import { getTokenValue } from '@atlaskit/tokens';
import { parseUserColor } from '@atlaskit/navigation-system/experimental/color-utils/parse-user-color';

import { reduceLightness, getLightnessPercentage } from '@atlassian/color-picker/color-utils';

type RGB = {
	r: number;
	g: number;
	b: number;
};

type CustomThemeOptions = {
	backgroundColorRaw: string | null | undefined;
	highlightColorRaw: string | null | undefined;
	isDarkMode: boolean;
	useServerValue?: boolean;
};

export type CustomThemeResult = {
	backgroundColor: RGB | null;
	highlightColor: RGB | null;
};

const NULL_THEME_RESULT = {
	backgroundColor: null,
	highlightColor: null,
} as const;

const DEFAULT_HIGHLIGHT_COLOR_CODES = [
	'0052CC', // ADG3,
	'0C66E4', // ADG4
];
const BACKGROUND_LIGHTNESS_MAX = 35;

/**
 * Generates a custom theme object with validated and processed colors.
 *
 * @param backgroundColorRaw - Background color string (hex, rgb, hsl, CSS variable)
 * @param highlightColorRaw - Highlight color string (hex, rgb, hsl, CSS variable)
 * @param isDarkMode - Whether to apply dark mode transformations
 *
 * @returns Theme object with RGB or null values for background and highlight colors
 */
export const getCustomTheme = ({
	backgroundColorRaw,
	highlightColorRaw,
	isDarkMode,
	useServerValue = false,
}: CustomThemeOptions): CustomThemeResult => {
	if (!backgroundColorRaw || !highlightColorRaw) {
		return NULL_THEME_RESULT;
	}

	const backgroundColor = parseColor(backgroundColorRaw, useServerValue);
	const highlightColor = parseColor(highlightColorRaw, useServerValue);

	if (isDefaultTheme(backgroundColor, highlightColor)) {
		return NULL_THEME_RESULT;
	}

	return {
		backgroundColor:
			isDarkMode && backgroundColor
				? applyDarkModeTransformation(backgroundColor)
				: backgroundColor,
		highlightColor,
	};
};

/**
 * Determines if the given colors match the default theme.
 */
const isDefaultTheme = (backgroundColor: RGB | null, highlightColor: RGB | null): boolean => {
	if (!backgroundColor || !highlightColor) {
		return false;
	}

	const currentDefaultBackground = parseColor(getTokenValue('elevation.surface', '#ffffff'));
	const isDefaultBackground = areColorsEqual(backgroundColor, currentDefaultBackground);

	const isDefaultHighlight = DEFAULT_HIGHLIGHT_COLOR_CODES.some((color) => {
		const currentDefaultColor = parseColor(getTokenValue('color.text.brand', `#${color}`));
		return areColorsEqual(highlightColor, currentDefaultColor);
	});

	return isDefaultBackground && isDefaultHighlight;
};

/**
 * Parses and validates a color string using platform utilities.
 * Returns RGB object or null if invalid.
 */
const parseColor = (colorToParse: string, useServerValue = false): RGB | null => {
	if (colorToParse.startsWith('rgba(') && colorToParse.split(',').length === 4) {
		colorToParse = `rgb(${colorToParse.substring(5, colorToParse.lastIndexOf(','))})`;
	} else if (colorToParse.startsWith('var(')) {
		colorToParse = getCSSVariableValue(colorToParse, useServerValue); // Will only get re-calculated if the variable is from ADS
	}

	return parseUserColor(colorToParse);
};

/**
 * Transforms a background color for dark mode by either using the dark surface token
 * or reducing lightness for custom colors.
 */
const getDarkModeBackgroundColor = (currentBackgroundColor: string): string => {
	if (currentBackgroundColor.toLowerCase() === '#ffffff') {
		// getTokenValue doesn't work in SSR thus hardcode fallback to current surface value, fix in: https://product-fabric.atlassian.net/browse/DSP-9781
		return getTokenValue('elevation.surface', '#1d2125');
	} else {
		const lightnessPercentage = getLightnessPercentage(currentBackgroundColor);
		if (lightnessPercentage === undefined) {
			return currentBackgroundColor;
		}
		return lightnessPercentage > BACKGROUND_LIGHTNESS_MAX
			? reduceLightness(currentBackgroundColor, BACKGROUND_LIGHTNESS_MAX)
			: currentBackgroundColor;
	}
};

const applyDarkModeTransformation = (color: RGB): RGB | null => {
	const hexColor = rgbToHex(color); // utility functions require hex colors
	const darkHex = getDarkModeBackgroundColor(hexColor);
	return parseUserColor(darkHex);
};

const areColorsEqual = (color1: RGB | null, color2: RGB | null): boolean => {
	if (!color1 || !color2) {
		return color1 === color2;
	}

	return color1.r === color2.r && color1.g === color2.g && color1.b === color2.b;
};

const rgbToHex = (rgb: { r: number; g: number; b: number }): string => {
	const toHex = (n: number) => {
		const hex = Math.round(n).toString(16);
		return hex.length === 1 ? `0${hex}` : hex;
	};
	return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`.toUpperCase();
};

const getCSSVariableValue = (variableExpression: string, useServerValue = false) => {
	const matcher = variableExpression.match(/var\(([^,\)]+)(,.*)?\)/);
	if (matcher) {
		const variable = matcher[1].trim();
		const fallback = matcher[2] ? matcher[2].replace(',', '').trim() : '';
		// During SSR and initial hydration, use fallback to prevent mismatch
		if (useServerValue || typeof document === 'undefined') {
			return fallback;
		}
		const value = window
			.getComputedStyle(document.documentElement)
			.getPropertyValue(variable)
			.trim();

		return value || fallback;
	}

	return '';
};
